/**
 * What one backend call is: method, path, query and body. The client turns it
 * into a URL and headers; callers never build either by hand.
 */

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export type QueryValue = string | number | boolean | null | undefined

/** One query parameter: a scalar, or an array sent as the key repeated once per item. */
type QueryParam = QueryValue | readonly QueryValue[]

export interface Endpoint {
  method: HttpMethod
  /** `'/v1/...'`, built with the {@link path} tag so interpolated segments are encoded. */
  path: string
  /**
   * Query parameters. `null`/`undefined` values are dropped, and an array
   * repeats its key (`?a=1&a=2`). Never put `?` in `path`.
   */
  query?: Record<string, QueryParam>
  /** JSON-serialised, unless it is `FormData` or a `Blob`, which go out as-is. */
  body?: unknown
  /** `'none'` for public pages: no `Authorization` header, and `getToken` is never called. Default `'required'`. */
  auth?: 'required' | 'none'
}

/**
 * Tagged template for endpoint paths: every interpolated value is
 * `encodeURIComponent`-ed, the literal parts are kept as written.
 *
 * `path\`/v1/mobile-apps/${id}\`` is safe for any `id`, including one with
 * `/`, `?` or `%` in it. Do not pre-encode a value you interpolate here, or it
 * is encoded twice.
 */
export function path(strings: TemplateStringsArray, ...values: Array<string | number>): string {
  return strings.reduce(
    (out, literal, i) => out + literal + (i < values.length ? encodeURIComponent(values[i]) : ''),
    ''
  )
}

/**
 * Builds a URL query string from an object, dropping `undefined`/`null` values.
 * An array value appends its key once per item.
 *
 * @returns the query with a leading `?`, or `''` when nothing is left.
 *
 * @example
 * buildQueryString({ school_id: '123', limit: 10, offset: undefined })
 * // '?school_id=123&limit=10'
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item !== undefined && item !== null) searchParams.append(key, String(item))
    }
  }

  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

/**
 * The full URL for an endpoint.
 *
 * The path must start with exactly one `/` and carry no query or fragment. That
 * is what keeps the bearer token on the backend: the legacy transport sent any
 * path containing `http` to that URL as-is, token included. Throws before the
 * client asks for a token.
 */
export function buildUrl(baseUrl: string, endpoint: Endpoint): string {
  const { path: endpointPath } = endpoint
  if (!/^\/(?!\/)/.test(endpointPath)) {
    throw new Error(`Backend path must start with a single '/': ${endpointPath}`)
  }
  if (/[?#]/.test(endpointPath)) {
    throw new Error(`Backend path must not carry a query or fragment, use Endpoint.query: ${endpointPath}`)
  }
  return `${baseUrl}${endpointPath}${buildQueryString(endpoint.query ?? {})}`
}
